import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { resetALL } from 'Redux/actions';
import { CLEAR_REDUX } from 'Core/common/redux/actionTypes';
import { AiMessageType, AIStage, EventTypes, InitializationState, AIMessageBubbleType, AIErrorType } from "../constants";
import {
  startAIChatSession,
  getAIChatHistory,
  fetchMoreMessages as fetchMoreMessagesService,
  cleanupSubscriptions,
  fetchSuggestionsByTopic,
  getAllUnreadCounts as getAllUnreadCountsService,
  markConversationAsRead as markConversationAsReadService,
  listConversations as listConversationsService,
} from "../services/aiChatService";

const initialState = {
  // Conversation
  conversationId: null,
  correlationId: null,
  
  // Browser session (unique per tab for event filtering)
  browserSessionId: null,
  
  // Messages
  items: [],
  nextToken: null,
  hasMoreMessages: false,
  latestThinkingTimeStamp: null,
  
  // Streaming state
  currentStage: AIStage.IDLE,
  stageMessage: null,
  isStreaming: false,
  lastStageTimestamp: {}, // Track last processed stage timestamp per correlationId
  
  // UI state
  loading: false,
  loadingState: null,
  error: null,
  
  // Subscription
  subscriptionRef: null,
  subscriptionConnected: false,
  
  // Initialization
  initializationState: InitializationState.IDLE,
  
  // Suggestions timer
  suggestionsTimer: null,
  suggestionsShown: false,
  fetchingSuggestions: false,
  
  // Unread message tracking
  unreadCount: 0,
  lastReadAt: null,
  unreadLoading: false,
  // Most recent conversationId from listConversations — cached to avoid repeat API calls
  latestListedConversationId: null,
};

/**
 * Send Message (use existing conversation)
 * - conversationId is passed in payload (from history or generated)
 * - Subscription is already active
 */
export const sendAIMessage = createAsyncThunk(
  'aiChatListRedux/sendAIMessage',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const {
        prompt,
        conversationId,
        userEmail,
        companyId,
        userId,
        domain,
      } = payload;

      if (!conversationId) {
        throw new Error('No conversationId provided');
      }

      // Get browser session ID from state for tab isolation
      const { browserSessionId } = getState().aiChatListRedux;
      console.log('🔍 [sendAIMessage] browserSessionId from state:', browserSessionId);

      const correlationId = uuidv4();

      // Send mutation with existing conversationId
      const { errorType } = await startAIChatSession({
        prompt,
        conversationId, // Use provided conversationId (from history or generated)
        correlationId, // New correlation for this message
        browserSessionId, // Browser session ID for event filtering
        userEmail,
        companyId,
        userId,
        domain,
      });

      // Subscription already active, will receive events

      return { 
        correlationId,
        errorType,
      };
    } catch (error) {
      console.error('❌ sendAIMessage error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const retryAIMessage = createAsyncThunk(
  'aiChatListRedux/retryAIMessage',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const {
        prompt,
        conversationId,
        userEmail,
        companyId,
        userId,
        domain,
      } = payload;

      // Get browser session ID from state for tab isolation
      const { browserSessionId } = getState().aiChatListRedux;

      const correlationId = uuidv4();

      // pending has already splice the error > send back message like normal
      const { errorType } = await startAIChatSession({
        prompt,
        conversationId, // Use provided conversationId (from history or generated)
        correlationId, // New correlation for this message
        browserSessionId, // Browser session ID for event filtering
        userEmail,
        companyId,
        userId,
        domain,
      });

      return { 
        correlationId,
        errorType,
      };

    } catch (error) {
      console.error('❌ retryAIMessage error:', error);
      return rejectWithValue(error?.message || '');
    }
  }
)

/**
 * Fetch Chat History (optional - for session resume)
 */
export const fetchChatHistory = createAsyncThunk(
  'aiChatListRedux/fetchChatHistory',
  async ({userId, messages}, { rejectWithValue, getState }) => {
    try {
      // Step 1: Resolve conversationId.
      // Only trust latestListedConversationId — a backend-confirmed value.
      // Never fall back to state.conversationId which may hold a temp UUID created
      // by initializeChat before this thunk runs (would skip listConversations and
      // orphan the user's real history).
      let resolvedConversationId = getState().aiChatListRedux.latestListedConversationId || null;

      if (!resolvedConversationId) {
        console.log('📋 [fetchChatHistory] No cached conversationId — calling listConversations...');
        const conversationsResult = await listConversationsService(userId, 1);
        const firstConversation = conversationsResult?.conversations?.[0];

        if (firstConversation?.conversationId) {
          resolvedConversationId = firstConversation.conversationId;
          console.log('✅ [fetchChatHistory] Found existing conversationId:', resolvedConversationId);
        } else {
          // Truly a new user — generate a fresh conversationId and return early.
          // No history call needed because there is nothing to fetch.
          resolvedConversationId = uuidv4();
          console.log('🆕 [fetchChatHistory] No conversations found, new conversationId:', resolvedConversationId);
          return {
            items: [],
            hasHistory: false,
            conversationId: resolvedConversationId,
          };
        }
      }

      const result = await getAIChatHistory(userId, 10, resolvedConversationId);
      
      console.log('📦 [fetchChatHistory] Raw result from API:', {
        hasItems: !!result?.items,
        itemCount: result?.items?.length || 0,
        hasNextToken: !!result?.nextToken,
      });
      
      if (!result || !result.items) {
        console.log('⚠️ [fetchChatHistory] No result or items, returning empty');
        return { 
          items: [], 
          hasHistory: false,
        };
      }

      // Transform backend format to frontend format
      let transformedItems = result.items
        .filter(item => {
          const isMessage = item.entityType === 'MESSAGE';
          if (!isMessage) {
            console.log('⏭️ [fetchChatHistory] Skipping non-MESSAGE entity:', item.entityType);
          }
          return isMessage;
        })
        .map(item => ({
          id: item.messageId || uuidv4(),
          message: item.message || '',
          sender: item.role === 'user' ? 'user' : 'bot', // Transform role to sender
          createdOn: item.createdAt || new Date().toISOString(),
          messageId: item.messageId,
          role: item.role,
        }))
        .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn)); // Sort by timestamp

      console.log('✅ [fetchChatHistory] Transformed history:', {
        count: transformedItems.length,
        hasHistory: transformedItems.length > 0,
        firstMessage: transformedItems[0]?.message?.substring(0, 50),
      });

      if (!result.nextToken) {
        // use epoch timestamp if there is history; if not, use current time for new conversation
        const welcomeMessage = createWelcomeMessage(messages, 
          transformedItems.length > 0 ? new Date(0).toISOString() : new Date().toISOString());
        transformedItems = [...welcomeMessage, ...transformedItems];

        console.log('✅ [fetchChatHistory] Append welcome:', {
          count: transformedItems.length,
          hasHistory: transformedItems.length > 0,
          firstMessage: transformedItems[0]?.message?.substring(0, 50),
        });
      }
      
      // Extract conversationId from result (backend returns camelCase conversationId)
      const conversationId = result.conversationId;
      
      return { 
        items: transformedItems, 
        hasHistory: transformedItems.length > 0,
        nextToken: result.nextToken,
        conversationId: conversationId, // Return conversationId from history
      };
    } catch (error) {
      const errorMessage = error.message || '';
      // Check if this is the expected backend not ready error
      if (errorMessage.includes('FieldUndefined') || errorMessage.includes('getChatHistory')) {
        console.warn('⚠️ [fetchChatHistory] Backend query not implemented, starting new conversation');
        return { 
          items: [], 
          hasHistory: false,
        };
      }
      
      // Log unexpected errors
      console.error('❌ [fetchChatHistory] Unexpected error:', error);
      return rejectWithValue(error.message || 'Failed to fetch history');
    }
  }
);

/**
 * Sync History From Other Session
 * Called when another browser tab completes streaming.
 * Merges new messages into existing state without replacing everything.
 */
export const syncHistoryFromOtherSession = createAsyncThunk(
  'aiChatListRedux/syncHistoryFromOtherSession',
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      console.log('🔄 [syncHistoryFromOtherSession] Fetching latest history...');
      const result = await getAIChatHistory(userId);
      
      if (!result?.items) {
        console.log('⚠️ [syncHistoryFromOtherSession] No items returned');
        return { candidateMessages: [] };
      }

      // Transform backend format to frontend format
      const transformedItems = result.items
        .filter(item => item.entityType === 'MESSAGE')
        .map(item => ({
          id: item.messageId || uuidv4(),
          message: item.message || '',
          sender: item.role === 'user' ? 'user' : 'bot',
          createdOn: item.createdAt || new Date().toISOString(),
          messageId: item.messageId,
          role: item.role,
        }))
        .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

      // Only pre-filter items that are already confirmed by messageId in state.
      // ALL content-based dedup is intentionally deferred to the reducer which uses
      // an upsert (one-to-one) strategy — this is the only approach that is correct
      // when the same question is asked legitimately multiple times in history.
      const existingMessageIds = new Set(
        getState().aiChatListRedux.items
          .filter(item => item.messageId)
          .map(item => item.messageId)
      );

      const candidateMessages = transformedItems.filter(
        item => !item.messageId || !existingMessageIds.has(item.messageId)
      );

      console.log('✅ [syncHistoryFromOtherSession] Candidate messages for reducer:', {
        totalFromBackend: transformedItems.length,
        alreadyConfirmed: transformedItems.length - candidateMessages.length,
        candidates: candidateMessages.length,
      });

      return { candidateMessages, conversationId: result.conversationId };
    } catch (error) {
      console.error('❌ [syncHistoryFromOtherSession] Error:', error);
      return rejectWithValue(error.message || 'Failed to sync history');
    }
  }
);

/**
 * Fetch More Messages (for scroll-to-top pagination)
 */
export const fetchMoreMessages = createAsyncThunk(
  'aiChatListRedux/fetchMoreMessages',
  async ({ conversationId, nextToken, messages }, { rejectWithValue }) => {
    try {
      const result = await fetchMoreMessagesService(conversationId, nextToken);
      
      if (!result || !result.messages) {
        return { 
          items: [], 
          nextToken: null,
        };
      }

      // Transform backend format to frontend format
      let transformedItems = result.messages
        .filter(item => item.entityType === 'MESSAGE')
        .map(item => ({
          id: item.messageId || uuidv4(),
          message: item.message || '',
          sender: item.role === 'user' ? 'user' : 'bot',
          createdOn: item.createdAt || new Date().toISOString(),
          messageId: item.messageId,
          role: item.role,
        }))
        .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

      console.log('✅ [fetchMoreMessages] Transformed messages:', {
        count: transformedItems.length,
        hasNextToken: !!result.nextToken,
      });

      if (!result.nextToken) {
        const welcomeMessage = createWelcomeMessage(messages, new Date(0).toISOString());
        transformedItems = [...welcomeMessage, ...transformedItems];
        console.log('✅ [fetchMoreMessages] Append welcome:', {
          count: transformedItems.length,
          hasHistory: transformedItems.length > 0,
          firstMessage: transformedItems[0]?.message?.substring(0, 50),
        });
      }
      
      return { 
        items: transformedItems, 
        nextToken: result.nextToken,
      };
    } catch (error) {
      console.error('❌ [fetchMoreMessages] Error:', error);
      return rejectWithValue(error.message || 'Failed to fetch more messages');
    }
  }
);

/**
 * Fetch Suggestions by Topic
 * Called by the 30-second timer
 */
export const fetchSuggestions = createAsyncThunk(
  'aiChatListRedux/fetchSuggestions',
  async ({ domain = 'GPST', topic = 'fleet', locale = 'EN', translations }, { rejectWithValue }) => {
    try {
      console.log('🔍 [fetchSuggestions] Fetching suggestions from backend...');
      
      const suggestions = await fetchSuggestionsByTopic({ domain, topic, locale });
      
      console.log('✅ [fetchSuggestions] Fetched suggestions:', { count: suggestions.length });
      
      return { suggestions, translations };
    } catch (error) {
      console.error('❌ [fetchSuggestions] Error:', error);
      return rejectWithValue(error.message || 'Failed to fetch suggestions');
    }
  }
);

/**
 * Fetch All Unread Counts for AI Chat
 * Gets the total unread message count for the user's AI conversations
 */
export const fetchAIUnreadCounts = createAsyncThunk(
  'aiChatListRedux/fetchAIUnreadCounts',
  async ({ userId }, { rejectWithValue, getState }) => {
    try {
      console.log('🔔 [fetchAIUnreadCounts] Fetching unread counts for userId:', userId);

      // Step 1: Resolve the latest conversationId.
      // Priority:
      //   1. Cached from a previous fetchAIUnreadCounts call (latestListedConversationId)
      //   2. Already in Redux from fetchChatHistory (which calls listConversations internally)
      //   3. Last resort: call listConversations directly (first ever open, no history yet)
      let latestConversationId = getState().aiChatListRedux.latestListedConversationId
        || getState().aiChatListRedux.conversationId
        || null;

      if (!latestConversationId) {
        const { conversations } = await listConversationsService(userId, 1);
        latestConversationId = conversations?.[0]?.conversationId || null;
        console.log('📋 [fetchAIUnreadCounts] Fetched latestConversationId from API:', latestConversationId);
      } else {
        console.log('📋 [fetchAIUnreadCounts] Reusing conversationId from state:', latestConversationId);
      }

      // Step 2: Pass conversationId to the API so the backend filters/sums server-side.
      // The returned array is already scoped to that conversation (or all if not provided).
      const unreadCounts = await getAllUnreadCountsService(userId, latestConversationId);

      // Step 3: Sum what the backend returned (server already filtered by conversationId).
      const totalUnread = unreadCounts.reduce((sum, item) => sum + (item.unreadCount || 0), 0);

      console.log('✅ [fetchAIUnreadCounts] Total unread:', totalUnread, { latestConversationId });

      return {
        unreadCounts,
        totalUnread,
        latestConversationId,
      };
    } catch (error) {
      console.error('❌ [fetchAIUnreadCounts] Error:', error);
      return rejectWithValue(error.message || 'Failed to fetch unread counts');
    }
  }
);

/**
 * Mark AI Conversation as Read
 * Resets unread count to 0 when user opens the AI chat
 */
export const markAIConversationAsRead = createAsyncThunk(
  'aiChatListRedux/markAIConversationAsRead',
  async ({ conversationId, lastMessageId, userId }, { rejectWithValue }) => {
    try {
      console.log('📖 [markAIConversationAsRead] Marking conversation as read:', { 
        conversationId, 
        lastMessageId, 
        userId 
      });
      
      // Skip if no conversationId or lastMessageId
      if (!conversationId || !lastMessageId) {
        console.warn('⚠️ [markAIConversationAsRead] Missing conversationId or lastMessageId');
        return { success: false, error: 'Missing required parameters' };
      }
      
      const result = await markConversationAsReadService(conversationId, lastMessageId, userId);
      
      console.log('✅ [markAIConversationAsRead] Result:', result);
      
      return result;
    } catch (error) {
      console.error('❌ [markAIConversationAsRead] Error:', error);
      return rejectWithValue(error.message || 'Failed to mark conversation as read');
    }
  }
);

/**
 * Initialize AI Chat with Loading Sequence
 * Shows: Skeleton (until connected) → LoadingDots → Suggestions
 * Waits for WebSocket subscription to connect before proceeding
 */
export const initializeAIChatUI = createAsyncThunk(
  'aiChatListRedux/initializeAIChatUI',
  async ({ translations, subscriptionConnectedPromise }, { dispatch }) => {
    try {
      // Phase 1: Show skeleton while waiting for WebSocket connection
      dispatch(setInitializationState(InitializationState.SKELETON));
      
      // Wait for subscription to connect (with timeout)
      const connectionTimeout = new Promise((resolve) => setTimeout(() => {
        resolve('timeout');
      }, 5000));
      
      await Promise.race([subscriptionConnectedPromise, connectionTimeout]);

      // Phase 2: Show loading dots (500ms)
      dispatch(setInitializationState(InitializationState.LOADING_DOTS));
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Phase 3: Show welcome message
      // Use current timestamp for new conversations (no history)
      const welcomeMessage = createWelcomeMessage(translations, new Date().toISOString());

      // Add welcome message directly to items array so it persists
      dispatch(addWelcomeMessageToItems(welcomeMessage));
      dispatch(setInitializationState(InitializationState.WELCOME_READY));

      return [...welcomeMessage];
    } catch (error) {
      throw error;
    }
  }
);

// Helper function for translations
const getTranslation = (message, translations) => {
  return translations[message] ?? message;
};

// Helper function to create welcome message
// timestamp: optional timestamp for welcome message (defaults to epoch for sorting first)
// Pass current time when creating for new conversations, epoch when prepending to history
const createWelcomeMessage = (translations, timestamp = null) => {
  return [
    {
      id: 'welcome-0',
      sender: 'bot',
      message: `${getTranslation("ai_welcome_greeting", translations)}<br><br>${getTranslation("ai_welcome_description", translations)}`,
      type: AiMessageType.WELCOME,
      createdOn: timestamp || new Date(0).toISOString(),
    },
    {
      id: 'welcome-1',
      sender: 'bot',
      message: `${getTranslation("ai_welcome_cta", translations)}`,
      type: AiMessageType.WELCOME,
      createdOn: timestamp || new Date(0).toISOString(),
    }
  ];
};

const aiChatListRedux = createSlice({
  name: 'aiChatListRedux',
  initialState,
  reducers: {
    /**
     * Add user message to items
     */
    addUserMessage: (state, action) => {
      state.items.push(action.payload);
    },

    /**
     * Handle real-time event from subscription
     */
    handleRealtimeEvent: (state, action) => {
      const { event } = action.payload;
      const eventType = event.__typename;

      // Stop processing events if input has been locked due to errors
      if (state.currentStage === AIStage.INPUT_LOCKED) {
        console.log('⛔ [handleRealtimeEvent] Ignoring event - rate limit exceeded:', eventType);
        return;
      }

      console.log('🔄 Processing event:', eventType, event);
      console.log('📋 [BEFORE] Current items state:', {
        itemsCount: state.items.length,
        currentStage: state.currentStage,
        items: state.items.map(item => ({
          id: item.id,
          sender: item.sender,
          message: item.message,
          correlationId: item.correlationId,
          isStreaming: item.isStreaming,
          type: item.type,
        })),
      });

      switch (eventType) {
        case EventTypes.STAGE: {
          // Sort stage events by timestamp to prevent processing out-of-order events
          // (e.g., delayed RETRIEVING event arriving after GENERATING)
          const currentStageTimestamp = state.lastStageTimestamp?.[event.correlationId];
          const eventTimestamp = new Date(event.timestamp).getTime();
          
          if (currentStageTimestamp && eventTimestamp < currentStageTimestamp) {
            console.log('⚠️ Ignoring out-of-order stage event:', {
              stage: event.stage,
              eventTime: event.timestamp,
              currentStage: state.currentStage,
              lastProcessedTime: new Date(currentStageTimestamp).toISOString(),
            });
            break;
          }
          
          // Store timestamp for this correlationId to prevent processing older events
          if (!state.lastStageTimestamp) {
            state.lastStageTimestamp = {};
          }
          state.lastStageTimestamp[event.correlationId] = eventTimestamp;
          
          // Update stage from backend
          state.currentStage = event.stage;
          // Use backend message directly (backend sends appropriate descriptions)
          state.stageMessage = event.message;
          state.loadingState = event.stage;

          // Find the placeholder bot message (added when API started)
          const placeholderIndex = state.items.findIndex(
            (item) => item.correlationId === event.correlationId && item.sender === 'bot'
          );

          if (event.stage === AIStage.GENERATING) {
            // For GENERATING stage, clear placeholder to receive tokens
            if (placeholderIndex !== -1) {
              // Clear previous stage message and prepare for token streaming
              state.items[placeholderIndex].message = '';
              state.items[placeholderIndex].temporaryMessage = '';
              state.items[placeholderIndex].isStreaming = true;
              // Remove stage type so it renders as normal message
              delete state.items[placeholderIndex].type;
              delete state.items[placeholderIndex].messageStage;
              // Set to loading dots until first token arrives
              state.items[placeholderIndex].bubbleType = AIMessageBubbleType.LOADING_DOTS;
            } else {
              // Create placeholder if it doesn't exist
              state.items.push({
                id: uuidv4(),
                message: '',
                sender: 'bot',
                createdOn: new Date().toISOString(),
                isStreaming: true,
                correlationId: event.correlationId,
                temporaryMessage: '',
                bubbleType: AIMessageBubbleType.LOADING_DOTS,
              });
            }
            state.isStreaming = true;
          } else if (event.stage === AIStage.ANALYZING || event.stage === AIStage.CONNECTING_KB_AGENT || event.stage === AIStage.RETRIEVING) {
            // For ANALYZING/CONNECTING_KB_AGENT/RETRIEVING stages, show stage message in placeholder
            if (placeholderIndex !== -1) {
              state.items[placeholderIndex].message = event.message;
              state.items[placeholderIndex].type = AiMessageType.STAGE;
              state.items[placeholderIndex].messageStage = event.stage;
              state.items[placeholderIndex].isStreaming = true;
              state.items[placeholderIndex].bubbleType = AIMessageBubbleType.STAGE;
            }
          }

          if (event.stage === AIStage.COMPLETE) {
            // Clean up timestamp tracking for this correlationId
            if (state.lastStageTimestamp) {
              delete state.lastStageTimestamp[event.correlationId];
            }
            state.isStreaming = false;
            state.loading = false;
            state.currentStage = AIStage.IDLE;
            
            // Clean up placeholder if it still exists
            if (placeholderIndex !== -1) {
              state.items[placeholderIndex].isStreaming = false;
            }
          }

          if (event.stage === AIStage.ERROR) {
            state.isStreaming = false;
            state.loading = false;
            state.error = event.message || 'Processing error';
            state.currentStage = AIStage.IDLE;
            
            // Update placeholder to show error - properly clean up loading state
            if (placeholderIndex !== -1) {
              state.items[placeholderIndex].message = event.message || 'Processing error';
              state.items[placeholderIndex].type = AiMessageType.ERROR;
              state.items[placeholderIndex].isStreaming = false;
              state.items[placeholderIndex].bubbleType = AIMessageBubbleType.TRY_AGAIN;
              // Remove temporaryMessage to hide loading dots
              delete state.items[placeholderIndex].temporaryMessage;
              console.log('❌ [STAGE Event] Set error message and cleared loading state at index:', placeholderIndex);
            }
          }
          break;
        }

        case EventTypes.TOKEN: {
          // Handle token with sequence ordering
          const aiMessageIndex = state.items.findIndex(
            (item) => item.correlationId === event.correlationId && item.sender === 'bot'
          );

          if (aiMessageIndex !== -1) {
            const currentMessage = state.items[aiMessageIndex];
            
            // Skip token if message is already finalized (not streaming)
            if (!currentMessage.isStreaming) {
              console.log('⚠️ [TOKEN Event] Skipping token for finalized message:', {
                correlationId: event.correlationId,
                sequence: event.sequence,
                currentMessageLength: currentMessage.message?.length,
              });
              break;
            }
            
            // Initialize token buffer if it doesn't exist
            if (!currentMessage.tokenBuffer) {
              currentMessage.tokenBuffer = {};
            }
            
            // Store token by sequence number
            currentMessage.tokenBuffer[event.sequence] = event.message;
            
            // Reconstruct message from ordered tokens
            const orderedTokens = Object.keys(currentMessage.tokenBuffer)
              .map(Number)
              .sort((a, b) => a - b)
              .map(seq => currentMessage.tokenBuffer[seq]);
            
            currentMessage.message = orderedTokens.join('');
            currentMessage.temporaryMessage = currentMessage.message;
            currentMessage.bubbleType = null; // use bubbleType default

            if (event.isFinal) {
              // Final token - clean up
              currentMessage.isStreaming = false;
              delete currentMessage.temporaryMessage;
              delete currentMessage.tokenBuffer;
            }
          } else {
            // Create new AI message if not found
            const tokenBuffer = {};
            tokenBuffer[event.sequence] = event.message;
            
            state.items.push({
              id: uuidv4(),
              message: event.message,
              sender: 'bot',
              createdOn: new Date().toISOString(),
              isStreaming: !event.isFinal,
              correlationId: event.correlationId,
              temporaryMessage: event.message,
              tokenBuffer: event.isFinal ? undefined : tokenBuffer,
            });
          }
          break;
        }

        case EventTypes.THINKING: {
          // Handle thinking event with sequence ordering (similar to TOKEN)
          const thinkingMessageIndex = state.items.findIndex(
            (item) => item.correlationId === event.correlationId && item.sender === 'bot'
          );

          if (thinkingMessageIndex !== -1) {
            const currentMessage = state.items[thinkingMessageIndex];

            // Ignore thinking event if its from a previous thinking seq
            if (currentMessage.thinkingTokenBuffer
              && state.latestThinkingTimeStamp
              && event.timestamp < state.latestThinkingTimeStamp) {
              console.warn("⚠️ [THINKING Event] Skipping event due to its timestamp is from a previous seq: ", {
                event: event.timestamp,
                state: state.latestThinkingTimeStamp,
              });
              break;
            }

            // Initialize token buffer if it doesn't exist
            if (!currentMessage.thinkingTokenBuffer) {
              currentMessage.thinkingTokenBuffer = {};
              state.latestThinkingTimeStamp = null;
            }

            // Skip if message is already finalized
            if (!currentMessage.isStreaming) {
              console.log('⚠️ [THINKING Event] Skipping chunk for finalized message:', {
                correlationId: event.correlationId,
                sequence: event.sequence,
              });
              break;
            }
            
            // Store chunk by sequence number
            currentMessage.thinkingTokenBuffer[event.sequence] = event.message;
            
            // Reconstruct message from ordered chunks
            const orderedChunks = Object.keys(currentMessage.thinkingTokenBuffer)
              .map(Number)
              .sort((a, b) => a - b)
              .map(seq => currentMessage.thinkingTokenBuffer[seq]);
            
            currentMessage.message = orderedChunks.join('');
            currentMessage.temporaryMessage = currentMessage.message;
            currentMessage.bubbleType = AIMessageBubbleType.THINKING;

            if (event.isFinal) {
              // Final thinking event of this thinking seq > update timestamp + empty tokenBuffer for next seq
              state.latestThinkingTimeStamp = event.timestamp;
              currentMessage.thinkingTokenBuffer = {};
            }
            
            console.log('💭 [THINKING Event] Updated message:', {
              sequence: event.sequence,
              isFinal: event.isFinal,
              messageLength: currentMessage.message?.length,
            });
          } else {
            // Create new thinking message if not found
            const thinkingTokenBuffer = {};
            thinkingTokenBuffer[event.sequence] = event.message;
            
            state.items.push({
              id: uuidv4(),
              message: event.message,
              sender: 'bot',
              createdOn: new Date().toISOString(),
              isStreaming: !event.isFinal,
              correlationId: event.correlationId,
              temporaryMessage: event.message,
              thinkingTokenBuffer: event.isFinal ? undefined : thinkingTokenBuffer,
              messageStage: "THINKING",
              bubbleType: AIMessageBubbleType.THINKING,
            });
            
            console.log('➕ [THINKING Event] Created new thinking message:', {
              sequence: event.sequence,
              isFinal: event.isFinal,
            });
          }
          break;
        }

        case EventTypes.ERROR: {
          console.log('❌ [ERROR Event] Processing error:', {
            error: event.error,
            correlationId: event.correlationId,
          });
          
          state.error = event.error;
          // Set stage based on error type
          state.currentStage = event.error === AIErrorType.RATE_LIMIT_EXCEEDED 
            ? AIStage.INPUT_LOCKED 
            : AIStage.IDLE;
          state.isStreaming = false;
          state.loading = false;

          // For rate limit errors, don't add error message to chat (shown in footer instead)
          if (event.error !== AIErrorType.RATE_LIMIT_EXCEEDED) {
            // Find placeholder and update it with error
            const errorPlaceholderIndex = state.items.findIndex(
              (item) => item.correlationId === event.correlationId && item.sender === 'bot'
            );

            if (errorPlaceholderIndex !== -1) {
              // Update existing placeholder with error
              state.items[errorPlaceholderIndex].message = `Error: ${event.error}`;
              state.items[errorPlaceholderIndex].type = AiMessageType.ERROR;
              state.items[errorPlaceholderIndex].bubbleType = AIMessageBubbleType.TRY_AGAIN;
              state.items[errorPlaceholderIndex].isStreaming = false;
              delete state.items[errorPlaceholderIndex].temporaryMessage;
              console.log('❌ [ERROR Event] Updated placeholder with error at index:', errorPlaceholderIndex);
            } else {
              // Add error message if no placeholder found
              state.items.push({
                id: uuidv4(),
                message: `Error: ${event.error}`,
                sender: 'bot',
                createdOn: new Date().toISOString(),
                type: AiMessageType.ERROR,
                bubbleType: AIMessageBubbleType.TRY_AGAIN,
                correlationId: event.correlationId, // insert correlationId only to new message since it wasn't found
              });
              console.log('➕ [ERROR Event] Added new error message (placeholder not found)');
            }
          } else {
            // For rate limit, remove any placeholder message
            let placeholderIndex = state.items.findIndex(
              (item) => item.correlationId === event.correlationId && item.sender === 'bot'
            );

            if (placeholderIndex === -1) {
              // Edge case: server may send RATE_LIMIT_EXCEEDED faster than SEND_CHAT_MESSAGE_MUTATION succeed > placeholder has no correlationId yet
              placeholderIndex = state.items.findIndex(
                item => item.sender === 'bot' && item.isStreaming && !item.correlationId
              );
            }

            if (placeholderIndex !== -1) {
              state.items.splice(placeholderIndex, 1);
              console.log('🗑️ [RATE_LIMIT_EXCEEDED] Removed placeholder message at index:', placeholderIndex);
            }
          }
          
          break;
        }

        default:
          console.warn('⚠️ Unknown event type:', eventType);
      }

      console.log('📋 [AFTER] Updated items state:', {
        itemsCount: state.items.length,
        items: state.items.map(item => ({
          id: item.id,
          sender: item.sender,
          message: item.message,
          correlationId: item.correlationId,
          isStreaming: item.isStreaming,
          type: item.type,
          temporaryMessage: item.temporaryMessage ? 'exists' : 'none',
        })),
        currentStage: state.currentStage,
        isStreaming: state.isStreaming,
      });
    },

    /**
     * Store subscription reference
     */
    setSubscription: (state, action) => {
      state.subscriptionRef = action.payload;
    },

    /**
     * Set subscription connection status
     */
    setSubscriptionConnected: (state, action) => {
      state.subscriptionConnected = action.payload;
    },

    /**
     * Set error
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.isStreaming = false;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Cleanup session (on unmount)
     */
    cleanupSession: (state) => {
      if (state.subscriptionRef) {
        cleanupSubscriptions(state.subscriptionRef);
        state.subscriptionRef = null;
      }
      state.subscriptionConnected = false;
      // Keep conversationId and items for history
    },

    /**
     * Reset all state
     */
    resetAIChat: () => initialState,

    /**
     * Set initialization state
     */
    setInitializationState: (state, action) => {
      state.initializationState = action.payload;
    },

    /**
     * Set conversationId manually (for history or generated)
     */
    setConversationId: (state, action) => {
      state.conversationId = action.payload;
    },

    /**
     * Set browser session ID (unique per browser tab)
     * Used for filtering streaming events between tabs
     */
    setBrowserSessionId: (state, action) => {
      state.browserSessionId = action.payload;
    },

    /**
     * Add welcome message to the beginning of the chat history
     * Uses epoch timestamp to ensure it sorts first when prepended to existing history
     */
    addWelcomeMessage: (state, action) => {
      const translations = action.payload;
      // Use epoch timestamp when prepending to history (ensures it's always first)
      const welcomeMessage = createWelcomeMessage(translations, new Date(0).toISOString());
      
      // Check if welcome message already exists
      const hasWelcome = state.items.some(item => item.type === AiMessageType.WELCOME);
      
      if (!hasWelcome) {
        // Prepend welcome message to the beginning
        state.items = [...welcomeMessage, ...state.items];
      }
    },

    /**
     * Add welcome message directly to items (for initial load)
     */
    addWelcomeMessageToItems: (state, action) => {
      const welcomeMessage = action.payload;
      
      // Check if welcome message already exists
      const hasWelcome = state.items.some(item => item.type === AiMessageType.WELCOME);
      
      if (!hasWelcome) {
        // Add welcome message as first item
        state.items = [...welcomeMessage];
      }
    },

    /**
     * Start suggestions timer (30 seconds)
     */
    startSuggestionsTimer: (state) => {
      console.log('⏱️ [startSuggestionsTimer] Timer started');
      // Timer will be managed in the component
      state.suggestionsShown = false;
    },

    /**
     * Stop suggestions timer
     */
    stopSuggestionsTimer: (state) => {
      console.log('⏹️ [stopSuggestionsTimer] Timer stopped');
      // Timer will be cleared in the component
    },

    /**
     * Mark suggestions as shown
     */
    setSuggestionsShown: (state, action) => {
      state.suggestionsShown = action.payload;
    },

    /**
     * Set unread count directly
     */
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },

    /**
     * Increment unread count (e.g., when a new message arrives while chat is closed)
     */
    incrementUnreadCount: (state, action) => {
      const increment = action.payload || 1;
      state.unreadCount = (state.unreadCount || 0) + increment;
    },

    /**
     * Reset unread count to 0 (e.g., when chat is opened)
     */
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // sendAIMessage
      .addCase(sendAIMessage.pending, (state, action) => {
        // Remove intro message and all suggestion items when user sends a message
        state.items = state.items.filter(item => item.type !== AiMessageType.SUGGESTION);
        
        // Add user message immediately when pending
        const userMessage = {
          id: uuidv4(),
          message: action.meta.arg.prompt,
          sender: 'user',
          createdOn: new Date().toISOString(),
        };
        state.items.push(userMessage);
        
        // Add AI placeholder to show 3 dots while API is pending
        // Will be updated by subscription with stage/message once API completes
        const aiPlaceholder = {
          id: uuidv4(),
          message: '',
          sender: 'bot',
          createdOn: new Date().toISOString(),
          isStreaming: true,
          temporaryMessage: '', // Shows 3 dots loading indicator
          bubbleType: AIMessageBubbleType.LOADING_DOTS,
        };
        state.items.push(aiPlaceholder);
        
        state.loading = true;
        state.error = null;
        state.isStreaming = true;
        state.currentStage = AIStage.ANALYZING;
        state.stageMessage = null;
        state.loadingState = null;
      })
      .addCase(sendAIMessage.fulfilled, handleMessageSentFulFilled)
      .addCase(sendAIMessage.rejected, handleMessageSentRejected)

      // retryAIMessage
      .addCase(retryAIMessage.pending, (state, action) => {
        const { errorId, prompt } = action.meta.arg;

        // Remove intro message and all suggestion items when user retries
        state.items = state.items.filter(item => item.type !== AiMessageType.SUGGESTION);

        // Find and remove the error message
        const errorIndex = state.items.findIndex(item => item.id === errorId);
        if (errorIndex !== -1) {
          const errorItem = state.items[errorIndex];
          const errorCorrelationId = errorItem.correlationId;
          
          // Remove the error message
          state.items.splice(errorIndex, 1);
          console.log('🗑️ [RETRY] Removed error message at index:', errorIndex);
          
          // Find and remove the failed user prompt
          let failedPromptIndex;
          if (errorCorrelationId) {
            // Primary path: match by correlationId
            failedPromptIndex = state.items.findIndex(
              item => item.sender === 'user' && item.correlationId === errorCorrelationId
            );
          } else {
            // Fallback path: correlationId is absent (e.g. sendAIMessage was rejected before
            // the thunk returned a correlationId). Find the nearest locally-created user
            // message above the error that has NO messageId. Messages synced from the server
            // always carry a messageId, so this safely singles out the pending local message.
            failedPromptIndex = -1;
            for (let i = errorIndex - 1; i >= 0; i--) {
              if (state.items[i].sender === 'user' && !state.items[i].messageId) {
                failedPromptIndex = i;
                break;
              }
            }
          }
          
          if (failedPromptIndex !== -1) {
            state.items.splice(failedPromptIndex, 1);
            console.log('🗑️ [RETRY] Removed failed user prompt at index:', failedPromptIndex);
          } else {
            console.warn('⚠️ [RETRY] Failed user prompt not found for errorCorrelationId:', errorCorrelationId);
          }
        }

        // Add the user message again as a NEW message at the bottom
        // (Original was never saved to DB since it failed)
        const userMessage = {
          id: uuidv4(),
          message: prompt,
          sender: 'user',
          createdOn: new Date().toISOString(),
        };
        state.items.push(userMessage);
        console.log('➕ [RETRY] Added user message as new at bottom');
        
        // Add AI placeholder to show 3 dots while API is pending
        const aiPlaceholder = {
          id: uuidv4(),
          message: '',
          sender: 'bot',
          createdOn: new Date().toISOString(),
          isStreaming: true,
          temporaryMessage: '', // Shows 3 dots loading indicator
          bubbleType: AIMessageBubbleType.LOADING_DOTS,
        };
        state.items.push(aiPlaceholder);
        console.log('➕ [RETRY] Added loading placeholder');
      })
      .addCase(retryAIMessage.fulfilled, handleMessageSentFulFilled)
      .addCase(retryAIMessage.rejected, handleMessageSentRejected)

      // fetchChatHistory
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Use SKELETON for initial history loading (cleaner than loading dots)
        state.initializationState = InitializationState.SKELETON;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        const { items, hasHistory, nextToken, conversationId } = action.payload;
        
        // Always persist the resolved conversationId (whether from history or newly generated).
        // This must happen regardless of whether there is history data.
        if (conversationId) {
          state.conversationId = conversationId;
        }

        const hasData = Array.isArray(items) && items.length > 0;
        const shouldShowHistory = hasHistory || hasData;
        
        if (shouldShowHistory && hasData) {
          // Load history items - ONLY if current items are empty (initial load)
          if (state.items.length === 0) {
            state.items = items;
            state.nextToken = nextToken;
            state.hasMoreMessages = !!nextToken;
          }
          state.initializationState = InitializationState.WELCOME_READY;
        } else {
          // No history - only clear items if they're empty
          if (state.items.length === 0) {
            state.items = [];
            state.nextToken = null;
            state.hasMoreMessages = false;
          }
          state.initializationState = InitializationState.IDLE;
        }
        
        state.loading = false;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load chat history';
        state.loading = false;
        if (state.items.length === 0) {
          state.items = [];
        }
        state.initializationState = InitializationState.WELCOME_READY;
      })

      // syncHistoryFromOtherSession - merge new messages from other browser tabs
      .addCase(syncHistoryFromOtherSession.fulfilled, (state, action) => {
        const { candidateMessages, conversationId } = action.payload;

        // Update conversationId to match the server's conversation.
        if (conversationId && state.conversationId !== conversationId) {
          console.log('🔄 [Sync] Updating conversationId from', state.conversationId, 'to', conversationId);
          state.conversationId = conversationId;
        }

        if (!candidateMessages || candidateMessages.length === 0) return;

        console.log('📥 [Redux] Processing', candidateMessages.length, 'candidate messages');

        // ─── Step 1: Classify current state items into stable vs tail ─────────────
        // "In-flight" correlationIds belong to error bubbles or streaming placeholders.
        // A local user-message is "tail" only when its correlationId is paired with
        // one of those — meaning the exchange is still pending or has failed.
        // Completed Q&A (even without messageId) must NOT be classified as tail so
        // they participate in content-matching below.
        const inFlightCorrIds = new Set(
          state.items
            .filter(item => item.type === AiMessageType.ERROR || item.isStreaming === true)
            .map(item => item.correlationId)
            .filter(Boolean)
        );

        const isTailItem = (item) => {
          if (item.type === AiMessageType.ERROR) return true;
          if (item.isStreaming === true) return true;
          if (
            item.sender === 'user' &&
            !item.messageId &&
            item.type !== AiMessageType.WELCOME &&
            item.type !== AiMessageType.SUGGESTION &&
            !!item.correlationId &&
            inFlightCorrIds.has(item.correlationId)
          ) return true;
          return false;
        };

        // ─── Step 2: Upsert candidates into stable items ──────────────────────────
        // For each candidate from the server we attempt a one-to-one content match
        // against existing STABLE items that have no messageId yet.
        //
        // WHY UPSERT instead of filter-out:
        //   Content-based filtering incorrectly removes legitimate repeated questions.
        //   e.g. User asks "what is maps?" on day 1 AND day 3 — both are valid turns.
        //   Upsert matches each candidate to an existing local item EXACTLY ONCE
        //   (via claimedIndices), so the first occurrence is stamped with a messageId
        //   (no duplicate) while the second occurrence has no local match and is
        //   appended as the genuinely new item.
        const claimedIndices = new Set();
        const genuinelyNew = [];

        for (const candidate of candidateMessages) {
          // Search for an unclaimed stable item with no messageId and matching content.
          let matchIdx = -1;
          for (let i = 0; i < state.items.length; i++) {
            if (claimedIndices.has(i)) continue;
            if (isTailItem(state.items[i])) continue;
            const existing = state.items[i];
            if (
              !existing.messageId &&
              existing.sender === candidate.sender &&
              (existing.message || '').trim() === (candidate.message || '').trim()
            ) {
              matchIdx = i;
              break;
            }
          }

          if (matchIdx !== -1) {
            // Stamp the existing local item with the server-assigned messageId.
            // This makes it "confirmed" without adding a duplicate entry.
            state.items[matchIdx].messageId = candidate.messageId;
            if (candidate.createdOn) {
              state.items[matchIdx].createdOn = candidate.createdOn;
            }
            claimedIndices.add(matchIdx);
            console.log('🔗 [Sync] Stamped messageId onto existing item:', candidate.sender, candidate.message?.substring(0, 40));
          } else {
            // No local match → this is a message from the other browser tab.
            genuinelyNew.push(candidate);
            console.log('➕ [Sync] Genuinely new message:', candidate.sender, candidate.message?.substring(0, 40));
          }
        }

        // If all candidates were in-place updates, no further re-composition needed.
        if (genuinelyNew.length === 0) {
          console.log('✅ [Sync] All candidates matched in place, no new items to append');
          return;
        }

        // ─── Step 3: Separate updated state into stable + tail for positioning ───
        const tailItems = state.items.filter(item => isTailItem(item));
        const stableItems = state.items.filter(item => !isTailItem(item));

        // ─── Step 4: Merge genuinely new items into stable, sort chronologically ─
        const mergedStable = [...stableItems, ...genuinelyNew];
        mergedStable.sort((a, b) => {
          const timeA = new Date(a.createdOn || 0).getTime();
          const timeB = new Date(b.createdOn || 0).getTime();
          return timeA - timeB;
        });

        // ─── Step 5: Auto-clean stale tail pairs already answered by genuinelyNew ─
        // If browser 1 answered a question that browser 2 had in-flight (failed),
        // the [tail: question + error] pair is now redundant — remove it so the user
        // does not see a duplicate question or a pointless retry button.
        const answeredContents = new Set();
        genuinelyNew.forEach((msg, idx) => {
          if (msg.sender !== 'user') return;
          const hasReply = genuinelyNew
            .slice(idx + 1)
            .some(m => m.sender === 'bot' && m.type !== AiMessageType.ERROR);
          if (hasReply) {
            answeredContents.add((msg.message || '').trim());
          }
        });

        const answeredCorrelationIds = new Set();
        const filteredTailItems = tailItems
          .filter(item => {
            if (item.sender === 'user' && answeredContents.has((item.message || '').trim())) {
              console.log('🧹 [Sync] Removing stale tail question (answered by sync):', item.message?.substring(0, 60));
              if (item.correlationId) answeredCorrelationIds.add(item.correlationId);
              return false;
            }
            return true;
          })
          .filter(item => {
            if (
              (item.type === AiMessageType.ERROR || item.isStreaming === true) &&
              item.correlationId &&
              answeredCorrelationIds.has(item.correlationId)
            ) {
              console.log('🧹 [Sync] Removing stale tail error/placeholder paired with answered question');
              return false;
            }
            return true;
          });

        // Tail items remain anchored to the bottom.
        state.items = [...mergedStable, ...filteredTailItems];
      })

      // fetchMoreMessages
      .addCase(fetchMoreMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMoreMessages.fulfilled, (state, action) => {
        const { items, nextToken } = action.payload;
        
        if (Array.isArray(items) && items.length > 0) {
          // Prepend older messages to the beginning of the list
          state.items = [...items, ...state.items];
        }
        
        state.nextToken = nextToken;
        state.hasMoreMessages = !!nextToken;
        state.loading = false;
      })
      .addCase(fetchMoreMessages.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load more messages';
        state.loading = false;
      })

      // initializeAIChatUI
      .addCase(initializeAIChatUI.pending, (state) => {
        state.initializationState = InitializationState.SKELETON;
      })
      .addCase(initializeAIChatUI.fulfilled, (state) => {
        state.initializationState = InitializationState.WELCOME_READY;
      })
      .addCase(initializeAIChatUI.rejected, (state) => {
        state.initializationState = InitializationState.WELCOME_READY;
        state.error = 'Failed to initialize chat';
      })

      // fetchSuggestions
      .addCase(fetchSuggestions.pending, (state) => {
        console.log('⏳ [fetchSuggestions.pending] Fetching suggestions...');
        state.fetchingSuggestions = true;
      })
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        console.log('✅ [fetchSuggestions.fulfilled] Suggestions received:', action.payload);
        state.fetchingSuggestions = false;
        state.suggestionsShown = true;
        
        // Add suggestions to the chat
        const { suggestions, translations } = action.payload;
        if (Array.isArray(suggestions) && suggestions.length > 0) {
          // Add intro message first (default bubbleType will show text)
          const introMessage = {
            id: uuidv4(),
            message: translations?.ai_suggestions_intro || "Hey! I'm doing great. How can I help you? Here are a few of the most common questions I can help you with:",
            sender: 'bot',
            createdOn: new Date().toISOString(),
            type: AiMessageType.SUGGESTION,
          };
          state.items.push(introMessage);
          
          // Then add suggestion items
          const suggestionItems = suggestions.map((suggestion) => ({
            id: suggestion.id || uuidv4(),
            message: suggestion.body,
            sender: 'bot',
            createdOn: new Date().toISOString(),
            type: AiMessageType.SUGGESTION,
            bubbleType: AIMessageBubbleType.SUGGESTION,
          }));
          
          // Add all suggestions to the chat
          state.items.push(...suggestionItems);
          console.log('📝 [fetchSuggestions.fulfilled] Added intro + suggestions to items:', suggestionItems.length + 1);
        }
      })
      .addCase(fetchSuggestions.rejected, (state, action) => {
        console.error('❌ [fetchSuggestions.rejected] Failed to fetch suggestions:', action.payload);
        state.fetchingSuggestions = false;
        // Don't show error to user, just silently fail
      })

      // fetchAIUnreadCounts
      .addCase(fetchAIUnreadCounts.pending, (state) => {
        state.unreadLoading = true;
      })
      .addCase(fetchAIUnreadCounts.fulfilled, (state, action) => {
        state.unreadLoading = false;
        state.unreadCount = action.payload.totalUnread || 0;
        // Cache the latest conversationId so subsequent calls skip listConversations
        if (action.payload.latestConversationId) {
          state.latestListedConversationId = action.payload.latestConversationId;
        }
      })
      .addCase(fetchAIUnreadCounts.rejected, (state) => {
        state.unreadLoading = false;
        // Don't show error to user, just keep previous count
      })

      // markAIConversationAsRead
      .addCase(markAIConversationAsRead.pending, (state) => {
        // Optimistically set unread count to 0
        state.unreadCount = 0;
      })
      .addCase(markAIConversationAsRead.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount || 0;
      })
      .addCase(markAIConversationAsRead.rejected, (state) => {
        // Keep unread count at 0 (optimistic update)
        // In case of error, will be corrected on next fetch
      })

      // Global reset
      .addCase(resetALL, () => initialState)
      
      // Handle logout via CLEAR_REDUX
      .addCase(CLEAR_REDUX, (state, action) => {
        // Reset AI chat state when clearRedux("") is called during logout
        if (action.payload?.option === "" || !action.payload?.option) {
          return initialState;
        }
        return state;
      });
  },
});

export const {
  addUserMessage,
  handleRealtimeEvent,
  setSubscription,
  setSubscriptionConnected,
  setError,
  clearError,
  cleanupSession,
  resetAIChat,
  setInitializationState,
  setConversationId,
  setBrowserSessionId,
  addWelcomeMessage,
  addWelcomeMessageToItems,
  startSuggestionsTimer,
  stopSuggestionsTimer,
  setSuggestionsShown,
  setUnreadCount,
  incrementUnreadCount,
  resetUnreadCount,
} = aiChatListRedux.actions;

// Legacy export for backward compatibility
export const setSessionId = setConversationId;

// Selectors
export const selectSubscriptionConnected = (state) => state.aiChatListRedux.subscriptionConnected;
export const selectInitializationState = (state) => state.aiChatListRedux.initializationState;
export const selectConversationId = (state) => state.aiChatListRedux.conversationId;
export const selectBrowserSessionId = (state) => state.aiChatListRedux.browserSessionId;
export const selectAIUnreadCount = (state) => state.aiChatListRedux.unreadCount || 0;
export const selectAILastReadAt = (state) => state.aiChatListRedux.lastReadAt;

export default aiChatListRedux.reducer;

const handleMessageSentRejected = (state, action) => {
  state.error = action.payload || 'Failed to send message';
  state.loading = false;
  state.isStreaming = false;
  state.currentStage = AIStage.IDLE;
  
  // Find and clean up the placeholder bot message that was added in pending
  const placeholderIndex = state.items.findIndex(
    item => item.sender === 'bot' && item.isStreaming
  );
  
  if (placeholderIndex !== -1) {
    // Update the existing placeholder with error message and preserve correlationId
    const correlationId = state.items[placeholderIndex].correlationId;
    state.items[placeholderIndex].message = 'Something went wrong — please try again shortly.';
    state.items[placeholderIndex].type = AiMessageType.ERROR;
    state.items[placeholderIndex].isStreaming = false;
    state.items[placeholderIndex].correlationId = correlationId;
    state.items[placeholderIndex].bubbleType = AIMessageBubbleType.TRY_AGAIN;
    // Remove temporaryMessage to hide loading dots
    delete state.items[placeholderIndex].temporaryMessage;
    console.log('❌ [sendAIMessage.rejected] Updated placeholder with error at index:', placeholderIndex);
  } else {
    // Fallback: Add error message if placeholder not found
    state.items.push({
      id: uuidv4(),
      message: 'Something went wrong — please try again shortly.',
      sender: 'bot',
      createdOn: new Date().toISOString(),
      type: AiMessageType.ERROR,
      bubbleType: AIMessageBubbleType.TRY_AGAIN,
    });
    console.log('❌ [sendAIMessage.rejected] Added new error message (placeholder not found)');
  }
};

const handleMessageSentFulFilled = (state, action) => {
  state.correlationId = action.payload.correlationId;
  
  // Update the placeholder with correlationId so subscription can find it
  const placeholderIndex = state.items.findIndex(
    item => item.sender === 'bot' && item.isStreaming && !item.correlationId
  );
  if (placeholderIndex !== -1) {
    state.items[placeholderIndex].correlationId = action.payload.correlationId;
  }

  // When sendAIMessage.fulfilled or retryAIMessage.fulfilled
  const lastUserMessageIndex = state.items.length - 2; // Last is placeholder, second-to-last is user
  if (lastUserMessageIndex >= 0 && state.items[lastUserMessageIndex].sender === 'user' && !state.items[lastUserMessageIndex].correlationId) {
    state.items[lastUserMessageIndex].correlationId = action.payload.correlationId;
    console.log('🔗 [Message Sent] Stored correlationId in user message');
  }

  // Handle error from BE
  if (action.payload.errorType) {
    switch (action.payload.errorType) {
      case AIErrorType.SCHEMA_VALIDATION_ERROR:
      case AIErrorType.INPUT_VALIDATION_ERROR:
        state.loading = false;
        state.error = action.payload.errorType;
        state.currentStage = AIStage.INPUT_LOCKED;
        // Remove the bot message
        state.items.splice(placeholderIndex, 1);
        break;
      
      case AIErrorType.SERVER_ERROR:
        state.loading = false;
        state.error = 'Server error';
        state.currentStage = AIStage.IDLE;
        state.items[placeholderIndex].message = 'Server error';
        state.items[placeholderIndex].type = AiMessageType.ERROR;
        state.items[placeholderIndex].isStreaming = false;
        state.items[placeholderIndex].bubbleType = AIMessageBubbleType.TRY_AGAIN;
        break;
    }
  }
  
  state.loading = false;
  // Keep isStreaming true - will be set to false when COMPLETE event arrives
}